import React, { useCallback } from 'react';
import { Image, Eye16 as IconShow16 } from '@cian/ui-kit';

import { IArticlesMainAttributesRubricSchema } from '../../repositories/journal/entities/journal/ArticlesMainAttributesRubricSchema';
import { IArticlesMainAttributesSchema } from '../../repositories/journal/entities/journal/ArticlesMainAttributesSchema';
import { IRubricLink, RubricButton } from './components/RubricButton';
import { formatDate } from '../../utils';
import { IconWithCount } from '../IconWithCount';
import { IconMessage16 } from '../Icons';
import * as s from './ListingItem.css';

export interface IListingItemRubric {
  /** Название */
  name: string;
  /** Ссылка */
  url: IRubricLink['url'];
}

export interface IListingItem
  extends Partial<Pick<IArticlesMainAttributesSchema, 'title' | 'image' | 'subtitle' | 'datePublish' | 'numViews'>> {
  rubric?: IListingItemRubric;
  numComments?: IArticlesMainAttributesSchema['commentsCount'];
  url: string;
  children?: React.ReactNode;
  onRubricButtonClick?(e: React.MouseEvent, url: IArticlesMainAttributesRubricSchema['url']): void;
  onClick?(e: React.MouseEvent): void;
}

/**
 * Компонент элемента списка
 */
export const ListingItem = ({
  title,
  url,
  subtitle,
  image,
  rubric,
  datePublish,
  numViews = 0,
  numComments = 0,
  children,
  onRubricButtonClick,
  onClick,
}: IListingItem) => {
  const handleArticleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (onClick) {
        onClick(e);
      }
    },
    [onClick],
  );

  const handleRubricClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      if (!rubric) {
        return;
      }

      if (onRubricButtonClick) {
        onRubricButtonClick(e, rubric.url);
      }
    },
    [onRubricButtonClick, rubric],
  );

  return (
    <div className={s['wrapper']}>
      <div className={s['content-wrapper']}>
        {(rubric || datePublish || numViews || numComments) && (
          <div className={s['headnote']}>
            {rubric && (
              <RubricButton url={rubric.url} onClick={handleRubricClick}>
                {rubric.name.toLowerCase()}
              </RubricButton>
            )}

            {datePublish && <div className={'date-publish'}>{formatDate(new Date(datePublish))}</div>}
            {numViews ? <IconWithCount count={numViews} icon={<IconShow16 />} /> : null}
            {numComments ? <IconWithCount count={numComments} icon={<IconMessage16 />} /> : null}
          </div>
        )}

        {title && (
          <a href={url} className={s['title']} title={title} onClick={handleArticleClick}>
            {title}
          </a>
        )}

        {subtitle && (
          <div className={s['subtitle']} title={subtitle}>
            {subtitle}
          </div>
        )}

        {children}
      </div>

      {image && (
        <a href={url} className={s['image-wrapper']} title={title} onClick={handleArticleClick}>
          <Image alt={title} borderRadius={0} objectFit="contain" preloader src={image} theme="light" title={title} />
        </a>
      )}
    </div>
  );
};
